/**
 * Anonymous student feedback preview for a faculty subject.
 */

import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import api from '../api/axios';
import Layout from '../components/Layout';

const LABELS = {
  TEACHING_QUALITY: 'Teaching quality',
  SUBJECT_KNOWLEDGE: 'Subject knowledge',
  COMMUNICATION: 'Communication',
  PUNCTUALITY: 'Punctuality',
  DOUBT_RESOLUTION: 'Doubt resolution',
  COURSE_COVERAGE: 'Course coverage',
  ASSESSMENT_FAIRNESS: 'Assessment fairness',
};

export default function FacultyFeedbackPreview() {
  const [params] = useSearchParams();
  const subjectId = params.get('subjectId');
  const cycleId = params.get('cycleId');
  const subjectName = params.get('subjectName') || 'Subject';
  const subjectCode = params.get('subjectCode') || '';
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!subjectId) {
      setError('Missing subject. Open Preview feedback from the faculty dashboard.');
      setLoading(false);
      return;
    }
    api
      .get(`/faculty/subject/${subjectId}/feedback`, { params: cycleId ? { cycleId } : {} })
      .then((res) => setData(res.data?.data || null))
      .catch((err) => setError(err.response?.data?.error?.message || 'Could not load anonymous feedback.'))
      .finally(() => setLoading(false));
  }, [subjectId, cycleId]);

  return (
    <Layout>
      <div className="fac-preview-page">
        <div className="genz-inner max-w-4xl">
          <p className="text-xs uppercase tracking-[0.2em] text-teal-200/80 mb-2">Anonymous student voices</p>
          <h1 className="text-3xl font-semibold text-white m-0">
            {data?.subject?.name || subjectName}
            {subjectCode || data?.subject?.code ? ` · ${subjectCode || data.subject.code}` : ''}
          </h1>
          <p className="mt-2 text-slate-300">
            Individual identities are hidden. You are viewing aggregated ratings and anonymous comments only.
          </p>
          {data?.cycle?.name ? <p className="text-sm text-teal-200/80 mt-1">{data.cycle.name}</p> : null}

          {error ? <div className="alert-error mt-4">{error}</div> : null}
          {loading ? (
            <div className="fac-preview-card mt-6">Loading anonymous feedback…</div>
          ) : data ? (
            <div className="space-y-5 mt-6">
              <div className="grid gap-3 sm:grid-cols-3">
                <div className="fac-preview-card">
                  <p className="text-xs uppercase tracking-wide text-slate-400 m-0">Responses</p>
                  <p className="text-3xl font-semibold text-white m-0 mt-1">{data.totals?.submitted ?? 0}</p>
                </div>
                <div className="fac-preview-card">
                  <p className="text-xs uppercase tracking-wide text-slate-400 m-0">Average</p>
                  <p className="text-3xl font-semibold text-white m-0 mt-1">{data.totals?.average ?? '—'}</p>
                </div>
                <div className="fac-preview-card">
                  <p className="text-xs uppercase tracking-wide text-slate-400 m-0">Department</p>
                  <p className="text-xl font-semibold text-white m-0 mt-1">{data.subject?.department || '—'}</p>
                </div>
              </div>

              <div className="fac-preview-card space-y-3">
                <h2 className="text-lg font-semibold text-white m-0">Criterion averages</h2>
                {(data.ratings || []).length === 0 ? (
                  <p className="text-slate-400">No ratings yet for this subject.</p>
                ) : (
                  (data.ratings || []).map((row) => (
                    <div key={row.criterion} className="genz-criterion">
                      <div className="flex justify-between gap-3">
                        <p className="text-white font-medium m-0">{LABELS[row.criterion] || row.criterion}</p>
                        <span>{row.avg_rating} · {row.response_count} voices</span>
                      </div>
                    </div>
                  ))
                )}
              </div>

              <div className="space-y-4">
                <h2 className="text-lg font-semibold text-white m-0">Anonymous submissions</h2>
                {(data.anonymousResponses || []).length === 0 ? (
                  <div className="fac-preview-card text-slate-400">No student submissions to preview yet.</div>
                ) : (
                  (data.anonymousResponses || []).map((entry) => (
                    <article key={`${entry.label}-${entry.submittedAt}`} className="fac-preview-card space-y-3">
                      <div className="flex flex-wrap items-center justify-between gap-2">
                        <p className="text-white font-semibold m-0">{entry.label}</p>
                        <p className="text-sm text-slate-400 m-0">
                          {entry.overallRating != null ? `${entry.overallRating} / 5` : '—'}
                          {entry.submittedAt ? ` · ${new Date(entry.submittedAt).toLocaleString()}` : ''}
                        </p>
                      </div>
                      <div className="grid gap-2 sm:grid-cols-2">
                        {(entry.ratings || []).map((rating) => (
                          <p key={rating.criterion} className="text-sm text-slate-300 m-0">
                            {LABELS[rating.criterion] || rating.criterion}: {rating.rating}/5
                          </p>
                        ))}
                      </div>
                      {entry.strengths ? <p className="text-sm text-slate-200 m-0"><span className="text-teal-200">Strengths: </span>{entry.strengths}</p> : null}
                      {entry.improvements ? <p className="text-sm text-slate-200 m-0"><span className="text-violet-200">Improvements: </span>{entry.improvements}</p> : null}
                      {entry.additionalComments ? <p className="text-sm text-slate-200 m-0"><span className="text-slate-400">Notes: </span>{entry.additionalComments}</p> : null}
                    </article>
                  ))
                )}
              </div>
            </div>
          ) : null}
        </div>
      </div>
    </Layout>
  );
}
